/** Tableau de bord administrateur : statistiques, étudiants, invitations, rôles. */
import { apiDelete, apiGet, apiPost } from './client';

/** Statistiques de la plateforme ; la forme détaillée est celle de la page admin. */
export function getPlatformStats<T>(learningMode?: string): Promise<T> {
  return apiGet<T>('/api/admin/stats', {
    query: { learning_mode: learningMode && learningMode !== 'all' ? learningMode : undefined },
  });
}

export function getStudents<T>(): Promise<T[]> {
  return apiGet<T[]>('/api/admin/students');
}

export interface Invite {
  id: string;
  email: string;
  status: 'pending' | 'used' | 'expired';
  expires_at: string;
  used_at: string | null;
  created_at: string;
  created_by_name: string | null;
  /** Lien copiable, présent seulement pour les invitations en attente (`null` sinon). */
  inviteUrl: string | null;
}

export function getInvites(): Promise<Invite[]> {
  return apiGet<{ invites: Invite[] }>('/api/admin/invites').then((data) => data?.invites ?? []);
}

export interface CreatedInvite {
  email: string;
  inviteUrl: string;
  /** Faux quand SMTP n'est pas configuré : l'admin copie le lien lui-même. */
  emailSent: boolean;
}

export function createInvite(params: { email: string; expiryHours: number }): Promise<CreatedInvite> {
  return apiPost<CreatedInvite>('/api/admin/invites', params).then((data) => ({
    email: data.email,
    inviteUrl: data.inviteUrl,
    emailSent: Boolean(data.emailSent),
  }));
}

export function deleteInvite(inviteId: string): Promise<void> {
  return apiDelete<void>(`/api/admin/invites/${encodeURIComponent(inviteId)}`);
}

export function setUserRole(userId: string, role: 'student' | 'admin'): Promise<void> {
  return apiPost<void>(`/api/admin/users/${encodeURIComponent(userId)}/role`, { role });
}

export interface PasswordResetLink {
  userId: string;
  username: string;
  /** Lien à transmettre à l'élève ; construit côté serveur à partir de `FRONTEND_URL`. */
  resetUrl: string;
  expiresAt: string;
  expiresInMinutes: number;
}

/** Lien de réinitialisation de mot de passe pour un élève (aucun e-mail envoyé ; annule les liens précédents). */
export function createPasswordResetLink(userId: string): Promise<PasswordResetLink> {
  return apiPost<PasswordResetLink>(`/api/admin/users/${encodeURIComponent(userId)}/password-reset-link`);
}
